import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
    FaWhatsapp,
    FaFacebookF,
    FaXTwitter,
    FaLinkedinIn,
    FaInstagram,
    FaLink
} from "react-icons/fa6";

export default function BlogDetail() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            setLoading(true);
            try {
                const res = await fetch(`https://beachhotelsdubai.com/api/blogs/${slug}`);
                const data = await res.json();
                setBlog(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        const fetchLatest = async () => {
            try {
                const res = await axios.get("https://beachhotelsdubai.com/api/blogs");
                const data = await res.json();
                setLatestBlogs(data);
            } catch (err) {
                console.error(err);
            }
        };

        fetchBlog();
        fetchLatest();
    }, [slug]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F172A] border-t-transparent"></div>
            </div>
        );
    }

    if (!blog) {
        return <p className="text-center py-20">Blog not found</p>;
    }

    const currentUrl = window.location.href;

    return (

        <>


            <Helmet>
                <title>{blog.meta_title || blog.title}</title>

                <meta
                    name="description"
                    content={blog.meta_description || blog.excerpt || blog.title}
                />

                <link
                    rel="canonical"
                    href={`https://beachhotelsdubai.com/blogs/${blog.slug}`}
                />

                {/* Open Graph */}
                <meta property="og:title" content={blog.meta_title || blog.title} />
                <meta
                    property="og:description"
                    content={blog.meta_description || blog.excerpt || blog.title}
                />
                <meta property="og:type" content="article" />
                <meta
                    property="og:url"
                    content={`https://beachhotelsdubai.com/blogs/${blog.slug}`}
                />

                <meta
                    property="og:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://beachhotelsdubai.com/images/default-og.png"
                    }
                />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blog.meta_title || blog.title} />
                <meta
                    name="twitter:description"
                    content={blog.meta_description || blog.excerpt || blog.title}
                />
                <meta
                    name="twitter:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://beachhotelsdubai.com/images/default-og.png"
                    }
                />

                {/* SEO */}
                <meta name="robots" content="index, follow" />
            </Helmet>
            <div>

                {/* ================= BANNER ================= */}
                <div className="relative h-[30vh] sm:h-[35vh] md:h-[40vh] w-full mb-8 sm:mb-12">
                    <img
                        src={`https://uaediscountedhotels.com/uploads/blogs/banners/${blog.banner_image}`}
                        alt={blog.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />

                    <div className="absolute inset-0 bg-black/50"></div>

                    <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex flex-col justify-center text-white">

                        <div className="mb-2 sm:mb-3">
                            <span className="bg-gradient-to-r from-[#0F172A] to-[#FF8C42] text-white text-[10px] sm:text-xs px-3 py-1 rounded-full">
                                {blog.category?.name}
                            </span>
                        </div>

                        <h1 className="text-xl sm:text-3xl md:text-5xl font-bold max-w-3xl leading-tight">
                            {blog.title}
                        </h1>

                        <div className="mt-3 text-xs sm:text-sm text-gray-200">
                            {new Date(blog.created_at).toDateString()}
                        </div>

                        <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-300 break-words">
                            <Link to="/" className="hover:text-white">Home</Link>
                            <span className="mx-1 sm:mx-2">/</span>
                            <Link to="/blogs" className="hover:text-white">Blogs</Link>
                            <span className="mx-1 sm:mx-2">/</span>
                            <span className="text-white">{blog.title}</span>
                        </div>
                    </div>
                </div>

                {/* ================= MAIN LAYOUT ================= */}
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

                    {/* MAIN CONTENT */}
                    <div className="lg:col-span-2">

                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                            <span className="bg-gradient-to-r from-[#0F172A] to-[#FF8C42] text-white text-[10px] sm:text-xs px-3 py-1 rounded-full">
                                {blog.category?.name}
                            </span>

                            <span className="text-xs sm:text-sm text-gray-500">
                                {new Date(blog.created_at).toDateString()}
                            </span>
                        </div>

                        <div
                            className="prose max-w-none prose-sm sm:prose-lg prose-headings:text-gray-800 prose-p:text-gray-600"
                            dangerouslySetInnerHTML={{ __html: blog.content }}
                        />

                        {/* ================= SOCIAL SHARE ================= */}
                        <div className="mt-8 sm:mt-12 border-t pt-5 sm:pt-6 pb-8 sm:pb-10">

                            <h3 className="text-base sm:text-lg font-semibold mb-4">
                                Share this article
                            </h3>

                            <div className="flex flex-wrap gap-2 sm:gap-3">

                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(currentUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                >
                                    <FaWhatsapp /> WhatsApp
                                </a>

                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                >
                                    <FaFacebookF /> Facebook
                                </a>

                                <a
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(blog.title)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-black text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                >
                                    <FaXTwitter /> X
                                </a>

                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(currentUrl);
                                        window.open("https://www.instagram.com/", "_blank");
                                        alert("Link copied!");
                                    }}
                                    className="flex items-center gap-2 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                    style={{
                                        background: "linear-gradient(45deg, #f58529, #dd2a7b, #8134af, #515bd4)"
                                    }}
                                >
                                    <FaInstagram /> Instagram
                                </button>

                                <a
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                >
                                    <FaLinkedinIn /> LinkedIn
                                </a>

                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(currentUrl);
                                        alert("Link copied!");
                                    }}
                                    className="flex items-center gap-2 bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm"
                                >
                                    <FaLink /> Copy
                                </button>

                            </div>
                        </div>

                    </div>

                    {/* SIDEBAR */}
                    <aside className="space-y-6">

                        <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-[0_10px_25px_rgba(255,107,53,0.1)] transition">
                            <h3 className="text-base sm:text-lg font-semibold mb-3">About This Blog</h3>
                            <p className="text-sm text-gray-600">
                                Discover travel insights, guides, and tips to help you plan your perfect trip.
                            </p>
                        </div>

                        <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border lg:sticky lg:top-24">
                            <h3 className="text-base sm:text-lg font-semibold mb-4">
                                Latest Blogs
                            </h3>

                            <div className="space-y-4">
                                {latestBlogs.slice(0, 5).map((item) => (
                                    <Link
                                        to={`/blogs/${item.slug}`}
                                        key={item.id}
                                        className="flex gap-3 group"
                                    >
                                        <img
                                            src={item.thumbnail_image}
                                            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md"
                                            alt=""
                                        />

                                        <div>
                                            <p className="text-xs sm:text-sm font-medium line-clamp-2 group-hover:text-[#0F172A]">
                                                {item.title}
                                            </p>
                                            <span className="text-[10px] sm:text-xs text-gray-400">
                                                {new Date(item.created_at).toDateString()}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-[#0F172A] to-[#FF8C42] text-white p-4 sm:p-5 rounded-xl shadow-lg mb-6 lg:mb-0">
                            <h3 className="text-base sm:text-lg font-semibold mb-2">
                                Explore More
                            </h3>
                            <p className="text-xs sm:text-sm">
                                Discover more blogs, destinations and travel guides.
                            </p>
                            <Link
                                to="/blogs"
                                className="inline-block mt-3 bg-white text-[#0F172A] px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium hover:shadow-md transition"
                            >
                                View All Blogs
                            </Link>
                        </div>

                    </aside>
                </div>
            </div>
        </>
    );
}